/**
 * EmergencyDeclarationBlock (Declaration & Notification Slice 1 #495 + Slice 3 #517).
 *
 * The operator-facing DECLARATION-OF-EMERGENCY surface, mounted inside the incident work
 * surface as a PARALLEL SIBLING to the incident-command + evacuation blocks — NOT a
 * child/precondition of either (a declaration is NOT required to order an evacuation).
 * "declare" in its CORRECT statutory home (doctrine-reference §2.1).
 *
 * records-not-confers: the COP RECORDS a declaration on behalf of the declaring authority;
 * it does NOT legally issue it. BOTH paths are wired:
 *  - MUNICIPAL (Slice 1): declarer = the Head of Council; "Notify the PEOC" reproduces the
 *    EMCPA s.4(3) copy + the PEOC Duty-Officer phone.
 *  - FIRST NATION (Slice 3): declarer = the Band Council (by Band Council Resolution) —
 *    COLLECTIVE, never a single head. Attribution is authority-first ("(Band Council)"). The
 *    BCR reference is OPTIONAL at record time (PERP §6.6.4's verbal-declaration allowance —
 *    "Band Council Resolution to follow") and can be backfilled later. Notification is the
 *    DUAL fan-out — the PEOC AND Indigenous Services Canada (ISC) — with NO EMCPA s.4(3) copy
 *    (PERP §1.3.2: First Nations are not subject to the EMCPA) and ADVISED, never "as required".
 */
import { useCallback, useEffect, useState, type JSX } from 'react';
import {
  DECLARE_EMERGENCY_LABEL,
  DECLARATION_OF_EMERGENCY_HEADING,
  TERMINATE_DECLARATION_LABEL,
  NOTIFY_PEOC_LABEL,
  FN_DUAL_NOTIFY_LABEL,
  FN_DUAL_NOTIFY_HELPER,
  BCR_FIELD_LABEL,
  BCR_TO_FOLLOW_LABEL,
  FN_SIGNATORY_FIELD_LABEL,
  DECLARATION_AUTHORITY_KIND_LABELS,
  DECLARATION_DECLARER_ROLE,
  PEOC_DUTY_OFFICER_PHONE,
  BILL25_S4_IN_FORCE,
  DECLARATION_REPORT_HEADING,
  DECLARATION_REPORT_RECIPIENT,
  DECLARATION_REPORT_S4_10_QUOTE,
  DECLARATION_REPORT_HELPER,
  DECLARATION_REPORT_CONTENTS_S4_10,
  DECLARATION_REPORT_PROVIDED_PREFIX,
  DECLARATION_REPORT_NOT_PROVIDED_LABEL,
  RECORD_REPORT_LABEL,
  PROVINCIAL_DECLARE_OPTION_LABEL,
  PROVINCIAL_DECLARER_LGIC,
  PROVINCIAL_DECLARER_PREMIER,
  OIC_REFERENCE_FIELD_LABEL,
  OIC_TO_FOLLOW_LABEL,
  PROVINCIAL_DECLARED_AREA_LABEL,
  PROVINCIAL_DECLARED_AREA_DEFAULT,
  DECLARATION_ASSEMBLY_REPORT_HEADING,
  DECLARATION_ASSEMBLY_REPORT_AUTHOR,
  DECLARATION_ASSEMBLY_REPORT_RECIPIENT,
  DECLARATION_ASSEMBLY_REPORT_TABLED_PREFIX,
  DECLARATION_ASSEMBLY_REPORT_RECORDED_SUFFIX,
  DECLARATION_ASSEMBLY_REPORT_NOT_RECORDED_LABEL,
  DECLARATION_ASSEMBLY_REPORT_DEADLINE_MARK_LABEL,
  ASSEMBLY_REPORT_REFERENCE_FIELD_LABEL,
  DECLARATION_S7010_QUOTE,
  DECLARATION_ASSEMBLY_REPORT_HELPER,
  RECORD_ASSEMBLY_REPORT_LABEL,
  provincialCloseoutDeadlineMark,
  authorityKindForCommunityType,
  declarationHeadline,
  declarationNotifyStatutorySurface,
  type DeclarationAuthorityKind,
  type CommunityFeatureProps,
  type EmergencyDeclarationRecord,
} from '@tsr/shared-types';
import {
  backfillDeclarationBcr,
  declareEmergency,
  fetchCommunityRoster,
  fetchEmergencyDeclarations,
  notifyPeocDeclaration,
  notifyPeocIscDeclaration,
  recordDeclarationReport,
  recordProvincialCloseout,
  terminateEmergencyDeclaration,
} from '../api';
import { getSessionId } from '../lib/operatorActions';
import { showToast } from '../lib/toast';

const inputCls =
  'w-full rounded border border-slate-700 bg-slate-900/70 px-2 py-1 text-[12px] text-slate-100 focus:border-violet-600 focus:outline-none';

/** #521 — the declaring-authority picker sentinel for "jurisdiction genuinely absent from the
 *  roster". Selecting it reveals the narrow free-text fallback — the ONLY remaining free-text
 *  path; every real jurisdiction is picked (never fat-fingered). */
const OTHER_VALUE = '__other__';
/** #614 — the DISTINCT provincial-declaration sentinel (NOT a roster jurisdiction — a provincial
 *  declaration under EMCPA s.7.0.1 is not tied to a community_type). Selecting it swaps the roster
 *  for the fixed Lieutenant Governor in Council / Premier declarer block. */
const PROVINCIAL_VALUE = '__provincial__';

function whenLabel(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export function EmergencyDeclarationBlock({
  incidentId,
  hazardType,
  defaultAuthorityName,
  bill25InForce = BILL25_S4_IN_FORCE,
}: {
  incidentId: string;
  hazardType?: string | null;
  /** Optional pre-seed for the declaring jurisdiction (operator can override). */
  defaultAuthorityName?: string | null;
  /** Bill-25 s.4(10) report UI gate. Defaults to the module flag (BILL25_S4_IN_FORCE); the
   *  prop exists ONLY so tests can force both flag states without mocking the module. When
   *  false EVERYTHING Slice-4 is ABSENT (no hint, no disabled row — legal accuracy + clutter
   *  law) and the notify surface reads the shipped s.4(3) copy. */
  bill25InForce?: boolean;
}): JSX.Element {
  const [declaration, setDeclaration] = useState<EmergencyDeclarationRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [declareOpen, setDeclareOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);

  // Declare-form fields.
  const [authorityKind, setAuthorityKind] = useState<DeclarationAuthorityKind>('municipal');
  const [authorityName, setAuthorityName] = useState(defaultAuthorityName ?? '');
  const [declarerName, setDeclarerName] = useState('');
  const [bcrReference, setBcrReference] = useState('');
  // Provincial (EMCPA s.7.0.1) declare fields.
  const [oicReference, setOicReference] = useState('');
  const [declaredArea, setDeclaredArea] = useState(PROVINCIAL_DECLARED_AREA_DEFAULT);
  const [provincialDeclarer, setProvincialDeclarer] = useState<'lgic' | 'premier'>('lgic');

  // #521 — the declaring-authority PICKER over the REAL FN/municipal roster (retires the
  // fat-fingerable free-text). `roster` = null while unloaded (incident-independent, cached
  // across incidents); `pickerValue` is '' (none) | a community id | OTHER_VALUE (free-text).
  const [roster, setRoster] = useState<CommunityFeatureProps[] | null>(null);
  const [pickerValue, setPickerValue] = useState('');

  // Notify-form fields.
  const [callConfirmed, setCallConfirmed] = useState(false);
  const [callNotes, setCallNotes] = useState('');
  const [contactPhone, setContactPhone] = useState<string | null>(null);

  // Backfill + terminate fields.
  const [backfillBcr, setBackfillBcr] = useState('');
  const [terminateReason, setTerminateReason] = useState('');
  const [terminatingBcr, setTerminatingBcr] = useState('');
  // #529 — the dormant s.4(10)(c) explanation capture (flag-gated report sub-block).
  const [reportExplanation, setReportExplanation] = useState('');
  // #616 — the PROVINCIAL closeout (EMCPA s.7.0.10): the date the Premier tabled the report to the
  // Assembly, the Sessional Paper number, and a short summary.
  const [closeoutTabledDate, setCloseoutTabledDate] = useState('');
  const [closeoutReference, setCloseoutReference] = useState('');
  const [closeoutSummary, setCloseoutSummary] = useState('');

  const refresh = useCallback(() => {
    let alive = true;
    setLoading(true);
    void fetchEmergencyDeclarations()
      .then((all) => {
        if (!alive) return;
        setDeclaration(all.find((d) => d.incident_id === incidentId) ?? null);
      })
      .catch(() => {
        /* a read failure leaves the block in its empty state */
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [incidentId]);

  useEffect(() => refresh(), [refresh]);

  // #521 — load the jurisdiction roster the first time the declare form opens. It is
  // incident-independent, so it is fetched once and cached across incidents (never reset by
  // the per-incident form reset below). A GENUINE read failure leaves an EMPTY roster and the
  // operator uses the free-text "other" fallback (honest degradation — the picker never blocks a
  // declare). An ABORT (Cancel / incident switch mid-load) is NOT a failure: it must leave roster
  // null so the next form-open refetches — else a cancel would permanently pin the empty state.
  useEffect(() => {
    if (!declareOpen || roster !== null) return;
    const ctrl = new AbortController();
    void fetchCommunityRoster(ctrl.signal)
      .then((fc) => setRoster(fc.features.map((f) => f.properties)))
      .catch(() => {
        if (!ctrl.signal.aborted) setRoster([]);
      });
    return () => ctrl.abort();
  }, [declareOpen, roster]);

  // Reset transient FORM state when the incident context changes — prevents a prior incident's
  // typed authority kind / name / BCR / call-confirm from leaking into the next incident's form
  // (the unkeyed-mount stale-form class, #507). The `declaration` itself is (re)loaded by refresh.
  useEffect(() => {
    setAuthorityKind('municipal');
    setAuthorityName(defaultAuthorityName ?? '');
    setPickerValue(''); // #521 — clear the picked jurisdiction (roster itself stays cached)
    setDeclarerName('');
    setBcrReference('');
    setOicReference('');
    setDeclaredArea(PROVINCIAL_DECLARED_AREA_DEFAULT);
    setProvincialDeclarer('lgic');
    setCallConfirmed(false);
    setCallNotes('');
    setContactPhone(null);
    setDeclareOpen(false);
    setNotifyOpen(false);
    setBackfillOpen(false);
    setTerminateOpen(false);
    setBackfillBcr('');
    setTerminateReason('');
    setTerminatingBcr('');
    setReportExplanation('');
  }, [incidentId, defaultAuthorityName]);

  const isFN = authorityKind === 'first_nation';
  const isProvincial = authorityKind === 'provincial';

  // #521 — the roster reduced to jurisdictions that map to a declaration path (FN/BCR or
  // municipal/EMCPA), each tagged with its DERIVED kind and sorted First Nations → Municipalities,
  // then by name. A community_type with no clean path is excluded here (routed via "Other").
  const pickable = (roster ?? [])
    .map((c) => ({ community: c, kind: authorityKindForCommunityType(c.community_type) }))
    .filter(
      (x): x is { community: CommunityFeatureProps; kind: DeclarationAuthorityKind } =>
        x.kind !== null,
    )
    .sort((a, b) =>
      a.kind === b.kind ? a.community.name.localeCompare(b.community.name) : a.kind.localeCompare(b.kind),
    );
  const otherMode = pickerValue === OTHER_VALUE;
  const provincialMode = pickerValue === PROVINCIAL_VALUE;
  const pickedCommunity =
    pickerValue && !otherMode && !provincialMode
      ? (roster?.find((c) => c.id === pickerValue) ?? null)
      : null;

  // Selecting a jurisdiction DERIVES the authority_kind from its type (never a manual guess) and
  // fills the real name; "Other" (or clearing) resets BOTH the name AND the kind to the neutral
  // form default, so the free-text fallback never inherits a stale last-picked kind (which could
  // otherwise submit e.g. a municipal name under a carried-over first_nation kind). "Province of
  // Ontario" (#614) is a FIXED provincial declaration (EMCPA s.7.0.1), not a roster jurisdiction.
  const onPickJurisdiction = (id: string): void => {
    setPickerValue(id);
    if (id === PROVINCIAL_VALUE) {
      setAuthorityKind('provincial');
      setAuthorityName(DECLARATION_AUTHORITY_KIND_LABELS.provincial); // 'Province of Ontario'
      return;
    }
    if (id === '' || id === OTHER_VALUE) {
      setAuthorityName('');
      setAuthorityKind('municipal');
      return;
    }
    const picked = roster?.find((c) => c.id === id);
    if (!picked) return;
    const kind = authorityKindForCommunityType(picked.community_type);
    if (kind) setAuthorityKind(kind);
    setAuthorityName(picked.name);
  };

  const doDeclare = (): void => {
    if (busy || authorityName.trim().length === 0) return;
    setBusy(true);
    // Provincial (EMCPA s.7.0.1): the declarer is the LGIC-by-OIC / Premier-urgent sub-choice
    // (written to declarer_name); the OIC reference + declared area are provincial-only.
    const provincialDeclarerName =
      provincialDeclarer === 'lgic' ? PROVINCIAL_DECLARER_LGIC : PROVINCIAL_DECLARER_PREMIER;
    void declareEmergency(
      {
        authority_kind: authorityKind,
        declaring_authority_name: authorityName.trim(),
        declarer_name: isProvincial
          ? provincialDeclarerName
          : declarerName.trim() || undefined,
        bcr_reference: isFN ? bcrReference.trim() || undefined : undefined,
        oic_reference: isProvincial ? oicReference.trim() || undefined : undefined,
        provincial_declared_area: isProvincial ? declaredArea.trim() || undefined : undefined,
        incident_id: incidentId,
        hazard_type: hazardType ?? undefined,
      },
      getSessionId(),
    )
      .then((rec) => {
        showToast(`Emergency declared — ${rec.declaration_number}.`, { variant: 'success' });
        setDeclaration(rec);
        setDeclareOpen(false);
      })
      .catch((e) => showToast((e as Error).message || 'Could not declare.', { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  const doTerminate = (): void => {
    if (busy || !declaration) return;
    setBusy(true);
    void terminateEmergencyDeclaration(declaration.id, getSessionId(), {
      reason: terminateReason.trim() || undefined,
      terminatingBcrReference: terminatingBcr.trim() || undefined,
    })
      .then((rec) => {
        showToast(`Declaration ${rec.declaration_number} terminated.`, { variant: 'success' });
        setDeclaration(rec);
        setTerminateOpen(false);
      })
      .catch((e) => showToast((e as Error).message || 'Could not terminate.', { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  const doNotify = (): void => {
    if (busy || !declaration) return;
    setBusy(true);
    void notifyPeocDeclaration(declaration.id, getSessionId(), {
      callConfirmed,
      callNotes: callNotes.trim() || undefined,
    })
      .then((res) => {
        setContactPhone(res.contact_phone);
        setDeclaration(res.declaration);
        showToast(
          `PEOC notified via ${res.delivery_channel}${
            res.destination_name ? ` (${res.destination_name})` : ''
          }.`,
          { variant: 'success' },
        );
        setNotifyOpen(false);
      })
      .catch((e) => showToast((e as Error).message || 'Could not notify PEOC.', { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  const doNotifyFn = (): void => {
    if (busy || !declaration) return;
    setBusy(true);
    void notifyPeocIscDeclaration(declaration.id, getSessionId(), {
      callConfirmed,
      callNotes: callNotes.trim() || undefined,
    })
      .then((res) => {
        setDeclaration(res.declaration);
        showToast(
          `Notified the PEOC and ISC — ${res.legs.map((l) => `${l.addressee.toUpperCase()} via ${l.delivery_channel}`).join(', ')}.`,
          { variant: 'success' },
        );
        setNotifyOpen(false);
      })
      .catch((e) =>
        showToast((e as Error).message || 'Could not notify the PEOC and ISC.', { variant: 'error' }),
      )
      .finally(() => setBusy(false));
  };

  const doBackfill = (): void => {
    if (busy || !declaration || backfillBcr.trim().length === 0) return;
    setBusy(true);
    void backfillDeclarationBcr(declaration.id, backfillBcr.trim(), getSessionId())
      .then((rec) => {
        showToast('Band Council Resolution recorded.', { variant: 'success' });
        setDeclaration(rec);
        setBackfillOpen(false);
        setBackfillBcr('');
      })
      .catch((e) =>
        showToast((e as Error).message || 'Could not record the BCR.', { variant: 'error' }),
      )
      .finally(() => setBusy(false));
  };

  // Bill-25 s.4(10): RECORD the post-termination report to the Minister (municipal, terminated).
  // The COP records it on behalf of the head of council — it does not submit it.
  const doRecordReport = (): void => {
    if (busy || !declaration) return;
    setBusy(true);
    void recordDeclarationReport(declaration.id, getSessionId(), {
      // #529 — the s.4(10)(c) explanation (dormant capture; the sub-block is flag-gated).
      explanation: reportExplanation.trim() || undefined,
    })
      .then((rec) => {
        showToast('Report to the Minister recorded.', { variant: 'success' });
        setDeclaration(rec);
      })
      .catch((e) =>
        showToast((e as Error).message || 'Could not record the report.', { variant: 'error' }),
      )
      .finally(() => setBusy(false));
  };

  // #616 — EMCPA s.7.0.10: RECORD that the Premier tabled the closeout report to the Assembly
  // (provincial, terminated). The COP records that it was tabled — it does not table it.
  const doRecordCloseout = (): void => {
    if (busy || !declaration) return;
    setBusy(true);
    // An empty date input → undefined (route defaults to now()); a `yyyy-mm-dd` input → an ISO
    // instant at UTC midnight so the payload passes the datetime({offset}) validation.
    const tabledAt = closeoutTabledDate.trim()
      ? new Date(`${closeoutTabledDate.trim()}T00:00:00Z`).toISOString()
      : undefined;
    void recordProvincialCloseout(declaration.id, getSessionId(), {
      tabledAt,
      assemblyReportReference: closeoutReference.trim() || undefined,
      assemblyReportSummary: closeoutSummary.trim() || undefined,
    })
      .then((rec) => {
        showToast('Premier’s report to the Assembly recorded.', { variant: 'success' });
        setDeclaration(rec);
      })
      .catch((e) =>
        showToast((e as Error).message || 'Could not record the closeout.', { variant: 'error' }),
      )
      .finally(() => setBusy(false));
  };

  const phoneToShow = contactPhone ?? PEOC_DUTY_OFFICER_PHONE;
  const declIsFN = declaration?.authority_kind === 'first_nation';
  const declIsProvincial = declaration?.authority_kind === 'provincial';
  // The flag-selected notify statutory surface — s.4(3) today; swaps to s.4(6) atomically when
  // the Bill-25 flag flips (the legally-atomic notify swap). Governs the panel copy + the
  // checkbox statutory citation together.
  const notifySurface = declarationNotifyStatutorySurface(bill25InForce);
  // The Bill-25 s.4(10) post-termination report sub-block gates on the flag AND a MUNICIPAL,
  // TERMINATED declaration (s.4(10) is a post-termination duty of a municipal head of council;
  // a First Nation is not subject to the EMCPA — PERP §1.3.2). OFF ⇒ EVERYTHING below is ABSENT.
  const showReportSubBlock =
    bill25InForce && declaration?.authority_kind === 'municipal' && declaration?.status === 'terminated';
  const reportProvidedAt = declaration?.post_termination_report_submitted_at ?? null;
  // #616 — the PROVINCIAL closeout (EMCPA s.7.0.10) sub-block. LIVE (NOT flag-gated — s.7.0.10 is
  // in-force EMCPA, unlike the Bill-25 s.4(10) municipal report): shown for a provincial, TERMINATED
  // declaration. Disjoint from showReportSubBlock (that requires municipal) and from the PEOC/ISC rows
  // (suppressed on provincial) — this block owns the provincial-terminated state exclusively.
  const showProvincialCloseout = declIsProvincial && declaration?.status === 'terminated';
  const closeoutTabledAt = declaration?.assembly_report_tabled_at ?? null;
  const closeoutDeadlineMark = provincialCloseoutDeadlineMark(declaration?.terminated_at ?? null);

  const startTerminate = (): void => {
    if (busy || !declaration) return;
    // Municipal has nothing extra to capture → terminate directly (unchanged UX).
    if (!declIsFN) {
      setBusy(true);
      void terminateEmergencyDeclaration(declaration.id, getSessionId())
        .then((rec) => {
          showToast(`Declaration ${rec.declaration_number} terminated.`, { variant: 'success' });
          setDeclaration(rec);
        })
        .catch((e) =>
          showToast((e as Error).message || 'Could not terminate.', { variant: 'error' }),
        )
        .finally(() => setBusy(false));
      return;
    }
    // First Nation → open the form to optionally record the terminating BCR.
    setTerminateOpen((v) => !v);
  };

  return (
    <section data-testid="emergency-declaration-block" className="space-y-1.5">
      <h4 className="text-[10px] uppercase tracking-wider text-slate-400">
        {DECLARATION_OF_EMERGENCY_HEADING}
      </h4>
      <p className="text-[10px] text-slate-500">
        Recorded on behalf of the declaring authority — the COP coordinates the record; it does not
        legally issue the declaration.
      </p>

      {loading ? (
        <p className="text-[11px] text-slate-500">Loading…</p>
      ) : !declaration ? (
        /* ── No declaration yet — the Declare affordance ─────────────────── */
        !declareOpen ? (
          <button
            type="button"
            data-testid="declare-emergency-open"
            onClick={() => setDeclareOpen(true)}
            className="w-full rounded border border-violet-500/60 py-1.5 text-[11px] text-violet-200 hover:bg-violet-600/20"
          >
            {DECLARE_EMERGENCY_LABEL}
          </button>
        ) : (
          <div className="space-y-1.5 rounded border border-slate-700 bg-slate-900/50 p-2">
            {/* #521 — the declaring-authority PICKER over the REAL FN/municipal roster. The
                authority KIND is DERIVED from the chosen jurisdiction (never fat-fingered), so the
                picker can't mislabel a First Nation as municipal. Options mirror the AgencyPicker
                idiom ("<Kind> — <name>"), reusing the pinned authority-kind labels verbatim. */}
            <label className="block text-[10px] text-slate-400">
              Declaring authority
              <select
                data-testid="declare-authority-picker"
                value={pickerValue}
                onChange={(e) => onPickJurisdiction(e.target.value)}
                className={inputCls}
              >
                <option value="">
                  {roster === null ? 'Loading jurisdictions…' : 'Select a jurisdiction…'}
                </option>
                {pickable.map(({ community, kind }) => (
                  <option key={community.id} value={community.id}>
                    {DECLARATION_AUTHORITY_KIND_LABELS[kind] + ' — ' + community.name}
                  </option>
                ))}
                {/* #614 — the DISTINCT provincial declaration (EMCPA s.7.0.1), a fixed option
                    (NOT a roster jurisdiction). */}
                <option value={PROVINCIAL_VALUE}>{PROVINCIAL_DECLARE_OPTION_LABEL}</option>
                <option value={OTHER_VALUE}>Other — jurisdiction not listed…</option>
              </select>
            </label>
            {/* Honest degradation — the roster is unreachable/empty: the operator uses "Other".
                (Not shown once the fixed Provincial option is chosen — it needs no roster.) */}
            {roster !== null && pickable.length === 0 && !provincialMode && (
              <p
                data-testid="declare-authority-roster-empty"
                className="text-[10px] text-amber-300/80"
              >
                Jurisdiction roster unavailable — use “Other” to enter it manually.
              </p>
            )}
            {/* Derived-kind confirmation (picker path) — makes the authority_kind + the FN
                Band-Council-by-BCR framing VISIBLE + auditable before declaring. */}
            {pickedCommunity && (
              <p
                data-testid="declare-authority-derived-kind"
                className="text-[10px] text-slate-300"
              >
                {DECLARATION_AUTHORITY_KIND_LABELS[authorityKind]} — declared by{' '}
                {DECLARATION_DECLARER_ROLE[authorityKind]}
              </p>
            )}
            {/* Narrow free-text FALLBACK — ONLY for a jurisdiction genuinely absent from the roster.
                The operator picks the declaration path (kind) explicitly and types the name. */}
            {otherMode && (
              <>
                <div className="flex gap-1" role="group" aria-label="Declaring authority kind">
                  {(['municipal', 'first_nation'] as const).map((k) => (
                    <button
                      key={k}
                      type="button"
                      data-testid={`authority-kind-${k}`}
                      onClick={() => setAuthorityKind(k)}
                      className={`flex-1 rounded border py-1 text-[11px] ${
                        authorityKind === k
                          ? 'border-violet-500 bg-violet-600/25 text-violet-100'
                          : 'border-slate-700 text-slate-400 hover:bg-slate-800'
                      }`}
                    >
                      {DECLARATION_AUTHORITY_KIND_LABELS[k]}
                    </button>
                  ))}
                </div>
                <label className="block text-[10px] text-slate-400">
                  {isFN ? 'Declaring First Nation' : 'Declaring municipality'}
                  <input
                    data-testid="declare-authority-name"
                    value={authorityName}
                    onChange={(e) => setAuthorityName(e.target.value)}
                    placeholder={
                      isFN ? 'First Nation not in the roster' : 'Municipality not in the roster'
                    }
                    className={inputCls}
                  />
                </label>
              </>
            )}
            {/* #614 — the DISTINCT provincial (EMCPA s.7.0.1) declarer block. Selecting "Province
                of Ontario" swaps the roster for this fixed Lieutenant Governor in Council / Premier
                block — the declarer sub-choice, the declared area, and the Order-in-Council ref. */}
            {provincialMode && (
              <div
                className="space-y-1.5 rounded border border-blue-600/40 bg-blue-950/20 p-2"
                data-testid="provincial-declare-block"
              >
                <p className="text-[10px] text-slate-300">
                  Province of Ontario — declared by {DECLARATION_DECLARER_ROLE.provincial} (EMCPA
                  s.7.0.1).
                </p>
                <div className="flex gap-1" role="group" aria-label="Provincial declarer">
                  {(
                    [
                      ['lgic', PROVINCIAL_DECLARER_LGIC],
                      ['premier', PROVINCIAL_DECLARER_PREMIER],
                    ] as const
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      data-testid={`provincial-declarer-${key}`}
                      onClick={() => setProvincialDeclarer(key)}
                      className={`flex-1 rounded border py-1 text-[11px] ${
                        provincialDeclarer === key
                          ? 'border-blue-500 bg-blue-600/25 text-blue-100'
                          : 'border-slate-700 text-slate-400 hover:bg-slate-800'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <label className="block text-[10px] text-slate-400">
                  {PROVINCIAL_DECLARED_AREA_LABEL}
                  <input
                    data-testid="provincial-declared-area"
                    value={declaredArea}
                    onChange={(e) => setDeclaredArea(e.target.value)}
                    placeholder="throughout Ontario | in [named part of Ontario]"
                    className={inputCls}
                  />
                </label>
                <label className="block text-[10px] text-slate-400">
                  {OIC_REFERENCE_FIELD_LABEL} — optional; omit for “{OIC_TO_FOLLOW_LABEL}”
                  <input
                    data-testid="provincial-oic-reference"
                    value={oicReference}
                    onChange={(e) => setOicReference(e.target.value)}
                    placeholder="Order in Council number + date (optional)"
                    className={inputCls}
                  />
                </label>
              </div>
            )}
            {!provincialMode && (
              <label className="block text-[10px] text-slate-400">
                {isFN
                  ? FN_SIGNATORY_FIELD_LABEL
                  : `Declared by (${DECLARATION_DECLARER_ROLE.municipal})`}
                <input
                  data-testid="declare-declarer-name"
                  value={declarerName}
                  onChange={(e) => setDeclarerName(e.target.value)}
                  placeholder={
                    isFN
                      ? 'on behalf of the Band Council (optional)'
                      : 'Head of Council name (optional)'
                  }
                  className={inputCls}
                />
              </label>
            )}
            {isFN && (
              <label className="block text-[10px] text-slate-400">
                {BCR_FIELD_LABEL} — optional; omit for “{BCR_TO_FOLLOW_LABEL}”
                <input
                  data-testid="declare-bcr-reference"
                  value={bcrReference}
                  onChange={(e) => setBcrReference(e.target.value)}
                  placeholder="BCR number + date (optional)"
                  className={inputCls}
                />
              </label>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="declare-emergency-submit"
                onClick={doDeclare}
                disabled={busy || authorityName.trim().length === 0}
                className="flex-1 rounded bg-violet-600 py-1.5 text-[11px] font-medium text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? 'Declaring…' : DECLARE_EMERGENCY_LABEL}
              </button>
              <button
                type="button"
                onClick={() => setDeclareOpen(false)}
                className="rounded border border-slate-700 px-3 py-1.5 text-[11px] text-slate-400 hover:bg-slate-800"
              >
                Cancel
              </button>
            </div>
          </div>
        )
      ) : (
        /* ── A declaration exists — record + actions ─────────────────────── */
        <div className="space-y-1.5" data-testid="emergency-declaration-detail">
          {/* Authority-first headline (misread guard) — First Nation or Provincial. */}
          {(declIsFN || declIsProvincial) && (
            <p
              className={`text-[12px] font-medium ${
                declIsProvincial ? 'text-blue-200' : 'text-fuchsia-200'
              }`}
              data-testid="declaration-headline"
            >
              {declarationHeadline(declaration.authority_kind, declaration.declaring_authority_name)}
            </p>
          )}
          <div className="flex items-center gap-2">
            <span className="font-mono text-[12px] text-slate-200">
              {declaration.declaration_number}
            </span>
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                declaration.status === 'declared'
                  ? 'bg-violet-600/25 text-violet-200'
                  : 'bg-slate-700/50 text-slate-400'
              }`}
              data-testid="declaration-status"
            >
              {declaration.status === 'declared' ? 'Declared' : 'Terminated'}
            </span>
          </div>
          <dl className="grid grid-cols-[7rem_1fr] gap-y-0.5 text-[11px]">
            <dt className="text-slate-400">Authority</dt>
            <dd className="text-slate-200">
              {declaration.declaring_authority_name}
              {' · '}
              {DECLARATION_AUTHORITY_KIND_LABELS[declaration.authority_kind]}
            </dd>
            <dt className="text-slate-400">Declared by</dt>
            <dd className="text-slate-200">
              {DECLARATION_DECLARER_ROLE[declaration.authority_kind]}
              {declaration.declarer_name
                ? ` — ${declIsFN ? 'Signatory ' : ''}${declaration.declarer_name}`
                : ''}
            </dd>
            {declIsFN && (
              <>
                <dt className="text-slate-400">{BCR_FIELD_LABEL}</dt>
                <dd className="text-slate-300" data-testid="declaration-bcr-state">
                  {declaration.bcr_reference ?? BCR_TO_FOLLOW_LABEL}
                </dd>
              </>
            )}
            {declIsProvincial && (
              <>
                <dt className="text-slate-400">{OIC_REFERENCE_FIELD_LABEL}</dt>
                <dd className="text-slate-300" data-testid="declaration-oic-state">
                  {declaration.oic_reference ?? OIC_TO_FOLLOW_LABEL}
                </dd>
                <dt className="text-slate-400">{PROVINCIAL_DECLARED_AREA_LABEL}</dt>
                <dd className="text-slate-300" data-testid="declaration-declared-area">
                  {declaration.provincial_declared_area ?? PROVINCIAL_DECLARED_AREA_DEFAULT}
                </dd>
              </>
            )}
            <dt className="text-slate-400">Declared</dt>
            <dd className="text-slate-300">{whenLabel(declaration.declared_at)}</dd>
            {declaration.status === 'terminated' && (
              <>
                <dt className="text-slate-400">Terminated</dt>
                <dd className="text-slate-300">{whenLabel(declaration.terminated_at)}</dd>
              </>
            )}
            {/* PEOC / ISC notification rows — municipal + First Nation only. A provincial
                declaration (EMCPA s.7.0.1) has NO notification leg (the Province is the
                provincial level); its closeout is the Premier's s.7.0.10 report to the Assembly. */}
            {!declIsProvincial && (
              <>
                <dt className="text-slate-400">PEOC</dt>
                <dd className="text-slate-300" data-testid="declaration-peoc-state">
                  {declaration.peoc_notified_at
                    ? `Notified ${whenLabel(declaration.peoc_notified_at)}`
                    : 'Not yet notified'}
                </dd>
                {declIsFN && (
                  <>
                    <dt className="text-slate-400">ISC</dt>
                    <dd className="text-slate-300" data-testid="declaration-isc-state">
                      {declaration.isc_notified_at
                        ? `Notified ${whenLabel(declaration.isc_notified_at)}`
                        : 'Not yet notified'}
                    </dd>
                  </>
                )}
              </>
            )}
          </dl>

          {/* Backfill the BCR — First Nation, when none is recorded yet ("to follow"). */}
          {declIsFN && declaration.status === 'declared' && !declaration.bcr_reference && (
            <div>
              {!backfillOpen ? (
                <button
                  type="button"
                  data-testid="backfill-bcr-open"
                  onClick={() => setBackfillOpen(true)}
                  className="rounded border border-fuchsia-500/50 px-2 py-1 text-[11px] text-fuchsia-200 hover:bg-fuchsia-600/20"
                >
                  Record the Band Council Resolution
                </button>
              ) : (
                <div className="space-y-1.5 rounded border border-fuchsia-600/40 bg-fuchsia-950/10 p-2">
                  <label className="block text-[10px] text-slate-400">
                    {BCR_FIELD_LABEL}
                    <input
                      data-testid="backfill-bcr-input"
                      value={backfillBcr}
                      onChange={(e) => setBackfillBcr(e.target.value)}
                      placeholder="BCR number + date"
                      className={inputCls}
                    />
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="backfill-bcr-submit"
                      onClick={doBackfill}
                      disabled={busy || backfillBcr.trim().length === 0}
                      className="flex-1 rounded bg-fuchsia-600 py-1.5 text-[11px] font-medium text-white hover:bg-fuchsia-500 disabled:opacity-50"
                    >
                      {busy ? 'Recording…' : 'Record BCR'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setBackfillOpen(false)}
                      className="rounded border border-slate-700 px-3 py-1.5 text-[11px] text-slate-400 hover:bg-slate-800"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {/* Notify affordance — municipal (PEOC) + First Nation (PEOC + ISC) only. A provincial
                declaration has NO notify leg (EMCPA s.7.0.1 — the Province is the provincial level). */}
            {declaration.status === 'declared' &&
              !declIsProvincial &&
              (declIsFN ? (
                <button
                  type="button"
                  data-testid="notify-peoc-isc-open"
                  onClick={() => setNotifyOpen((v) => !v)}
                  className="rounded border border-amber-500/60 px-2 py-1 text-[11px] text-amber-200 hover:bg-amber-600/20"
                >
                  {FN_DUAL_NOTIFY_LABEL}
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="notify-peoc-open"
                  onClick={() => setNotifyOpen((v) => !v)}
                  className="rounded border border-amber-500/60 px-2 py-1 text-[11px] text-amber-200 hover:bg-amber-600/20"
                >
                  {NOTIFY_PEOC_LABEL}
                </button>
              ))}
            <a
              data-testid="declaration-pdf-link"
              href={`/api/emergency-declarations/${declaration.id}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="rounded border border-slate-600 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800"
            >
              Download declaration PDF
            </a>
            {declaration.status === 'declared' && (
              <button
                type="button"
                data-testid="terminate-declaration"
                onClick={startTerminate}
                disabled={busy}
                className="rounded border border-slate-600 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800 disabled:opacity-50"
              >
                {TERMINATE_DECLARATION_LABEL}
              </button>
            )}
          </div>

          {/* Terminate (First Nation) — optionally record the terminating BCR. */}
          {terminateOpen && declIsFN && declaration.status === 'declared' && (
            <div
              className="space-y-1.5 rounded border border-slate-700 bg-slate-900/50 p-2"
              data-testid="terminate-fn-panel"
            >
              <label className="block text-[10px] text-slate-400">
                Terminating Band Council Resolution (optional)
                <input
                  data-testid="terminate-fn-bcr"
                  value={terminatingBcr}
                  onChange={(e) => setTerminatingBcr(e.target.value)}
                  placeholder="BCR number + date (optional)"
                  className={inputCls}
                />
              </label>
              <label className="block text-[10px] text-slate-400">
                Reason (optional)
                <input
                  data-testid="terminate-fn-reason"
                  value={terminateReason}
                  onChange={(e) => setTerminateReason(e.target.value)}
                  placeholder="e.g. threat abated"
                  className={inputCls}
                />
              </label>
              <button
                type="button"
                data-testid="terminate-fn-submit"
                onClick={doTerminate}
                disabled={busy}
                className="w-full rounded bg-slate-700 py-1.5 text-[11px] font-medium text-white hover:bg-slate-600 disabled:opacity-50"
              >
                {busy ? 'Terminating…' : TERMINATE_DECLARATION_LABEL}
              </button>
            </div>
          )}

          {/* ── Notify the PEOC (municipal) — the #490 bridge + the s.4(3) call ─────── */}
          {notifyOpen && !declIsFN && declaration.status === 'declared' && (
            <div
              className="space-y-1.5 rounded border border-amber-600/40 bg-amber-950/20 p-2"
              data-testid="notify-peoc-panel"
            >
              <p className="text-[11px] font-medium text-amber-200">{NOTIFY_PEOC_LABEL}</p>
              <p className="text-[10px] leading-snug text-slate-300" data-testid="notify-statutory-copy">
                {notifySurface.statutory_addressee}
                <br />
                {notifySurface.live_route}.
              </p>
              <p className="text-[11px] text-slate-200">
                PEOC Duty Officer (24/7):{' '}
                <a
                  href={`tel:${phoneToShow.replace(/[^0-9]/g, '')}`}
                  className="font-mono text-amber-200 underline"
                  data-testid="peoc-phone"
                >
                  {phoneToShow}
                </a>
              </p>
              <label className="block text-[10px] text-slate-400">
                Call notes (optional)
                <textarea
                  data-testid="notify-call-notes"
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  rows={2}
                  className={inputCls}
                />
              </label>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-300">
                <input
                  type="checkbox"
                  data-testid="notify-call-confirmed"
                  checked={callConfirmed}
                  onChange={(e) => setCallConfirmed(e.target.checked)}
                />
                Confirm the {notifySurface.cite} call to the PEOC Duty Officer was placed
              </label>
              <button
                type="button"
                data-testid="notify-peoc-submit"
                onClick={doNotify}
                disabled={busy}
                className="w-full rounded bg-amber-600 py-1.5 text-[11px] font-medium text-white hover:bg-amber-500 disabled:opacity-50"
              >
                {busy ? 'Notifying…' : NOTIFY_PEOC_LABEL}
              </button>
            </div>
          )}

          {/* ── Notify the PEOC + ISC (First Nation) — the dual government fan-out ───── */}
          {notifyOpen && declIsFN && declaration.status === 'declared' && (
            <div
              className="space-y-1.5 rounded border border-amber-600/40 bg-amber-950/20 p-2"
              data-testid="notify-peoc-isc-panel"
            >
              <p className="text-[11px] font-medium text-amber-200">{FN_DUAL_NOTIFY_LABEL}</p>
              <p className="text-[10px] leading-snug text-slate-300" data-testid="fn-dual-notify-helper">
                {FN_DUAL_NOTIFY_HELPER}
              </p>
              <p className="text-[11px] text-slate-200">
                PEOC Duty Officer (24/7):{' '}
                <a
                  href={`tel:${PEOC_DUTY_OFFICER_PHONE.replace(/[^0-9]/g, '')}`}
                  className="font-mono text-amber-200 underline"
                  data-testid="peoc-phone-fn"
                >
                  {PEOC_DUTY_OFFICER_PHONE}
                </a>
              </p>
              <label className="block text-[10px] text-slate-400">
                Notification notes (optional)
                <textarea
                  data-testid="notify-fn-notes"
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  rows={2}
                  className={inputCls}
                />
              </label>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-300">
                <input
                  type="checkbox"
                  data-testid="notify-fn-confirmed"
                  checked={callConfirmed}
                  onChange={(e) => setCallConfirmed(e.target.checked)}
                />
                Confirm the notification call(s) to the PEOC and ISC were placed
              </label>
              <button
                type="button"
                data-testid="notify-peoc-isc-submit"
                onClick={doNotifyFn}
                disabled={busy}
                className="w-full rounded bg-amber-600 py-1.5 text-[11px] font-medium text-white hover:bg-amber-500 disabled:opacity-50"
              >
                {busy ? 'Notifying…' : FN_DUAL_NOTIFY_LABEL}
              </button>
            </div>
          )}

          {/* ── Reporting to Minister (Bill-25 s.4(10)) — DORMANT, flag-gated ─────────
              Renders ONLY when BILL25_S4_IN_FORCE is on AND the declaration is MUNICIPAL +
              TERMINATED (s.4(10) is a post-termination duty of a municipal head of council; a
              First Nation is not subject to the EMCPA — PERP §1.3.2). Flag OFF ⇒ ABSENT entirely
              (no hint, no disabled row). The COP RECORDS the report on behalf of the head of
              council — it does not submit it (never "Submit / File / Send"). */}
          {showReportSubBlock && (
            <div
              className="space-y-1.5 rounded border border-violet-600/40 bg-violet-950/20 p-2"
              data-testid="report-to-minister-block"
            >
              <p className="text-[11px] font-medium text-violet-200">
                {DECLARATION_REPORT_HEADING}
                <span className="ml-1 text-[10px] font-normal text-slate-400">(EMCPA s.4(10))</span>
              </p>
              <dl className="grid grid-cols-[6rem_1fr] gap-y-0.5 text-[11px]">
                <dt className="text-slate-400">Author</dt>
                <dd className="text-slate-200">{DECLARATION_DECLARER_ROLE.municipal}</dd>
                <dt className="text-slate-400">Recipient</dt>
                <dd className="text-slate-200">{DECLARATION_REPORT_RECIPIENT}</dd>
                <dt className="text-slate-400">Report</dt>
                <dd className="text-slate-300" data-testid="report-to-minister-state">
                  {reportProvidedAt
                    ? `${DECLARATION_REPORT_PROVIDED_PREFIX} ${whenLabel(reportProvidedAt)}`
                    : DECLARATION_REPORT_NOT_PROVIDED_LABEL}
                </dd>
              </dl>
              <p className="text-[10px] leading-snug text-slate-400">
                {DECLARATION_REPORT_S4_10_QUOTE} {DECLARATION_REPORT_HELPER}
              </p>
              <details className="text-[10px] text-slate-400">
                <summary className="cursor-pointer text-slate-300">The report shall include</summary>
                <ol className="ml-4 list-[lower-alpha] space-y-0.5 pt-1">
                  {DECLARATION_REPORT_CONTENTS_S4_10.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ol>
              </details>
              {!reportProvidedAt && (
                <>
                  {/* #529 — the s.4(10)(c) explanation (optional; captured on record). The
                      (10)(d) "any other prescribed information" awaits its prescribing regulation
                      — captured at the API level, no UI field until the shape is defined. */}
                  <label className="block text-[10px] text-slate-400">
                    Explanation (s.4(10)(c)) — optional
                    <textarea
                      data-testid="report-explanation"
                      value={reportExplanation}
                      onChange={(e) => setReportExplanation(e.target.value)}
                      rows={2}
                      placeholder="Why the head of council declared the emergency (recorded for the report)"
                      className={inputCls}
                    />
                  </label>
                  <button
                    type="button"
                    data-testid="record-report-submit"
                    onClick={doRecordReport}
                    disabled={busy}
                    className="w-full rounded bg-violet-600 py-1.5 text-[11px] font-medium text-white hover:bg-violet-500 disabled:opacity-50"
                  >
                    {busy ? 'Recording…' : RECORD_REPORT_LABEL}
                  </button>
                </>
              )}
            </div>
          )}

          {/* ── Provincial closeout — report to the Assembly (EMCPA s.7.0.10) — LIVE ────
              Renders for a provincial, TERMINATED declaration (NOT flag-gated — s.7.0.10 is
              in-force EMCPA, unlike the Bill-25 s.4(10) municipal report). The Premier tables the
              report to the Assembly; the COP RECORDS that it was tabled — it does NOT table it
              (never "Table / Submit / File / Send"). NO "due"/"overdue" state: the COP does not
              hold the Assembly's sitting calendar, so the 120-day MARK is shown beside the verbatim
              not-in-session limb, never a breached-duty assertion. */}
          {showProvincialCloseout && (
            <div
              className="space-y-1.5 rounded border border-blue-600/40 bg-blue-950/20 p-2"
              data-testid="provincial-closeout-block"
            >
              <p className="text-[11px] font-medium text-blue-200">
                {DECLARATION_ASSEMBLY_REPORT_HEADING}
              </p>
              <dl className="grid grid-cols-[7rem_1fr] gap-y-0.5 text-[11px]">
                <dt className="text-slate-400">Author</dt>
                <dd className="text-slate-200">{DECLARATION_ASSEMBLY_REPORT_AUTHOR}</dd>
                <dt className="text-slate-400">Tabled in</dt>
                <dd className="text-slate-200">{DECLARATION_ASSEMBLY_REPORT_RECIPIENT}</dd>
                <dt className="text-slate-400">Report</dt>
                <dd className="text-slate-300" data-testid="provincial-closeout-state">
                  {closeoutTabledAt
                    ? `${DECLARATION_ASSEMBLY_REPORT_TABLED_PREFIX} ${whenLabel(closeoutTabledAt)} ${DECLARATION_ASSEMBLY_REPORT_RECORDED_SUFFIX}`
                    : DECLARATION_ASSEMBLY_REPORT_NOT_RECORDED_LABEL}
                </dd>
                {closeoutDeadlineMark && (
                  <>
                    <dt className="text-slate-400">
                      {DECLARATION_ASSEMBLY_REPORT_DEADLINE_MARK_LABEL}
                    </dt>
                    <dd className="text-slate-300" data-testid="provincial-closeout-mark">
                      {closeoutDeadlineMark}
                    </dd>
                  </>
                )}
                {declaration.assembly_report_reference && (
                  <>
                    <dt className="text-slate-400">{ASSEMBLY_REPORT_REFERENCE_FIELD_LABEL}</dt>
                    <dd className="text-slate-300" data-testid="provincial-closeout-reference">
                      {declaration.assembly_report_reference}
                    </dd>
                  </>
                )}
              </dl>
              <p className="text-[10px] leading-snug text-slate-400">
                {DECLARATION_S7010_QUOTE} {DECLARATION_ASSEMBLY_REPORT_HELPER}
              </p>
              {declaration.assembly_report_summary && (
                <p
                  className="text-[10px] leading-snug text-slate-300"
                  data-testid="provincial-closeout-summary"
                >
                  {declaration.assembly_report_summary}
                </p>
              )}
              {!closeoutTabledAt && (
                <>
                  <label className="block text-[10px] text-slate-400">
                    Date tabled in the Assembly — optional
                    <input
                      type="date"
                      data-testid="closeout-tabled-date"
                      value={closeoutTabledDate}
                      onChange={(e) => setCloseoutTabledDate(e.target.value)}
                      className={inputCls}
                    />
                  </label>
                  <label className="block text-[10px] text-slate-400">
                    {ASSEMBLY_REPORT_REFERENCE_FIELD_LABEL} — optional
                    <input
                      type="text"
                      data-testid="closeout-reference"
                      value={closeoutReference}
                      onChange={(e) => setCloseoutReference(e.target.value)}
                      placeholder="e.g. Sessional Paper No. 512"
                      className={inputCls}
                    />
                  </label>
                  <label className="block text-[10px] text-slate-400">
                    Summary — optional
                    <textarea
                      data-testid="closeout-summary"
                      value={closeoutSummary}
                      onChange={(e) => setCloseoutSummary(e.target.value)}
                      rows={2}
                      placeholder="Short summary of the report tabled in the Assembly"
                      className={inputCls}
                    />
                  </label>
                  <button
                    type="button"
                    data-testid="record-closeout-submit"
                    onClick={doRecordCloseout}
                    disabled={busy}
                    className="w-full rounded bg-blue-600 py-1.5 text-[11px] font-medium text-white hover:bg-blue-500 disabled:opacity-50"
                  >
                    {busy ? 'Recording…' : RECORD_ASSEMBLY_REPORT_LABEL}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
